import { flexRender } from "@tanstack/react-table"
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { TableHeader, TableRow, TableHead, cn, Checkbox } from 'twyne-new.ui';

import { useGrid } from '@Components/Grid/context';
import { useTableResizeContext } from '@Components/tableResize/context';
import { useColumnWidth } from '@Components/tableResize/hooks';

import { useTableContext } from "./context"

export default function DefaultTableHeader({ onResize }) {
  const table = useTableContext()
  const headerGroups = table.getHeaderGroups()
  return <TableHeader sticky>
    {headerGroups.map(({ id, headers }) => <TableRow key={id}>
      {/* <HeaderCheckbox table={table} /> */}
      {headers.map(({ id, column, isPlaceholder, getContext }, headerIndex) => <Column
        key={id}
        headerIndex={headerIndex}
        isPlaceholder={isPlaceholder}
        columnDef={column.columnDef}
        canSort={column.getCanSort()}
        onSort={column.getToggleSortingHandler()}
        isSorted={column.getIsSorted()}
        context={getContext()}
        onResize={onResize}
      />)}
    </TableRow>)
    }
  </TableHeader>
}

function _HeaderCheckbox({ table }) {
  const { rows } = table.getRowModel()
  const rowIds = rows.map(({ id, original }) => `${original?.id ?? id}`)
  const { rowsSelected, setRowsSelected } = useGrid()
  const allRowsSelected = rowIds.length > 0 && rowIds.every(id => rowsSelected.includes(id))

  const handleSelectAll = checkedValue => {
    const checked = typeof checkedValue === 'boolean' ? checkedValue : !!checkedValue?.target?.checked

    if (checked) {
      setRowsSelected(prevRowsSelected => Array.from(new Set([...prevRowsSelected, ...rowIds])))
      return
    }

    setRowsSelected([])
  }

  return <TableHead className='w-4'>
    <Checkbox value={allRowsSelected} onChange={handleSelectAll} />
  </TableHead>
}

function TableHeaderSort({ order, canSort }) {
  if (!canSort) {
    return null
  }

  const Icon = {
    asc: ChevronUp,
    desc: ChevronDown,
    default: ChevronsUpDown,
  }[order || 'default']

  return <Icon size={10} className='twy-text-base-neutral hover:twy-text-base-accent' />
}

function Column({ headerIndex, isPlaceholder, context, canSort, onSort, isSorted, onResize, columnDef }) {
  const { definitions: { enableTableResize = true } } = useGrid()
  if (!enableTableResize) {
    return <StaticColumn
      isPlaceholder={isPlaceholder}
      context={context}
      canSort={canSort}
      onSort={onSort}
      isSorted={isSorted}
      columnDef={columnDef}
    />
  }

  return <ResizableColumn
    headerIndex={headerIndex}
    isPlaceholder={isPlaceholder}
    context={context}
    canSort={canSort}
    onSort={onSort}
    isSorted={isSorted}
    onResize={onResize}
    columnDef={columnDef}
  />
}

function StaticColumn({ isPlaceholder, context, canSort, onSort, isSorted, columnDef }) {
  const { header, className } = columnDef

  if (isPlaceholder) {
    return null
  }

  return <ColumnHeader
    className={cn(canSort && 'cursor-pointer', className)}
    onClick={onSort}
    isSorted={isSorted}
    canSort={canSort}
    enableColumnResize={false}
  >
    {flexRender(header, context)}
  </ColumnHeader>
}

function ResizableColumn({ headerIndex, isPlaceholder, context, canSort, onSort, isSorted, onResize, columnDef }) {
  const { activeResizeColumn, resizedColumns } = useTableResizeContext()
  const columnWidth = useColumnWidth({ columnIndex: headerIndex })
  const { header, className, enableColumnResize = true } = columnDef
  const canBreakHeaderText = activeResizeColumn === headerIndex || resizedColumns.includes(headerIndex)

  if (isPlaceholder) {
    return null
  }

  return <ColumnHeader
    className={cn(canSort && 'cursor-pointer', className)}
    onClick={onSort}
    isSorted={isSorted}
    canSort={canSort}
    style={columnWidth}
    isResizing={activeResizeColumn === headerIndex}
    canBreakHeaderText={canBreakHeaderText}
    enableColumnResize={enableColumnResize}
    onStartResize={event => onResize?.({ columnIndex: headerIndex, event })}
  >
    {flexRender(header, context)}
  </ColumnHeader>
}

function ColumnHeader({ children, className, onClick, isSorted, canSort, style, onStartResize, isResizing, canBreakHeaderText = false, enableColumnResize }) {
  const { definitions: { enableTableResize = true } } = useGrid()

  return <TableHead className={cn(
    'relative group whitespace-nowrap',
    canBreakHeaderText && 'whitespace-normal break-all',
    className,
  )} style={style}>
    <ColumnResizer
      canResize={enableColumnResize && enableTableResize}
      isResizing={isResizing}
      onStartResize={onStartResize}
    />
    <div className={cn('flex gap-1 items-center', className)} onClick={onClick}>
      {children} <TableHeaderSort order={isSorted} canSort={canSort} />
    </div>
  </TableHead>
}

function ColumnResizer({ canResize, onStartResize, isResizing }) {
  if (!canResize) {
    return null
  }

  return <div
    onMouseDown={onStartResize}
    className={cn(
      'absolute top-0 h-full w-[3px] cursor-col-resize select-none touch-none',
      isResizing ? 'twy-bg-accent opacity-100' : 'twy-subtle-neutral opacity-0 group-hover:opacity-100',
      'right-0',
    )}
  />
}
